import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const defaultDdi = "55";

const sanitizeNumber = (value) => (value || "").replace(/\D/g, "");
const sanitizeDdiInput = (value) => {
  const raw = (value || "").replace(/\D/g, "");
  return raw.slice(0, 3);
};
const sanitizePhoneInput = (value, ddiDigits = "") => {
  const limit = ddiDigits === defaultDdi ? 11 : 13;
  return (value || "").replace(/\D/g, "").slice(0, limit);
};
const buildPhoneValue = (ddiDigits, phoneDigits, fallbackDdi = defaultDdi) =>
  phoneDigits ? `${ddiDigits || fallbackDdi}${phoneDigits}` : "";
const getPhoneValidation = (ddiDigits, numeroDigits) => {
  if (!ddiDigits) {
    return { valid: false, errorDdi: "Informe o DDI" };
  }

  if (!numeroDigits) {
    return { valid: false, errorNumero: "Informe o numero do celular" };
  }

  const totalDigits = `${ddiDigits}${numeroDigits}`.length;

  if (ddiDigits === defaultDdi) {
    if (numeroDigits.length !== 10 && numeroDigits.length !== 11) {
      return {
        valid: false,
        errorNumero: "Para DDI 55, o numero deve ter 10 ou 11 digitos.",
      };
    }

    if (totalDigits !== 12 && totalDigits !== 13) {
      return {
        valid: false,
        errorNumero:
          "Para DDI 55, o total de digitos (DDI + numero) deve ser 12 ou 13.",
      };
    }

    return { valid: true };
  }

  if (totalDigits < 8 || totalDigits > 15) {
    return {
      valid: false,
      errorNumero:
        "Para DDI diferente de 55, o total de digitos (DDI + numero) deve ter de 8 a 15.",
    };
  }

  return { valid: true };
};

const ModalCadastroGerente = ({
  open,
  onClose,
  onConfirm,
  saving = false,
  success = false,
  errorMessage = "",
  imobiliarias = [],
  loadingImobiliarias = false,
}) => {
  const [nome, setNome] = useState("");
  const [celularDdi, setCelularDdi] = useState(defaultDdi);
  const [celularNumero, setCelularNumero] = useState("");
  const [imobiliariaId, setImobiliariaId] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  const imobiliariasOrdenadas = useMemo(() => {
    return [...imobiliarias].sort((a, b) =>
      (a.nome || "").localeCompare(b.nome || "")
    );
  }, [imobiliarias]);

  useEffect(() => {
    if (!open) return;
    setNome("");
    setCelularDdi(defaultDdi);
    setCelularNumero("");
    setImobiliariaId("");
    setValidationErrors({});
  }, [open]);

  const celularDdiDigits = sanitizeNumber(celularDdi);
  const celularNumeroDigits = sanitizeNumber(celularNumero);
  const nomeFinal = (nome || "").trim();
  const phoneValidation = getPhoneValidation(
    celularDdiDigits,
    celularNumeroDigits
  );
  const canSubmit = Boolean(
    nomeFinal && imobiliariaId && phoneValidation.valid
  );
  const celularFinal = buildPhoneValue(
    celularDdiDigits,
    celularNumeroDigits,
    defaultDdi
  );
  const celularSanitizado = sanitizeNumber(celularFinal);

  const handleConfirm = async () => {
    const errors = {};

    if (!nomeFinal) {
      errors.nome = "Informe o nome do gerente";
    }

    if (phoneValidation.errorNumero) {
      errors.celularNumero = phoneValidation.errorNumero;
    }

    if (phoneValidation.errorDdi) {
      errors.celularDdi = phoneValidation.errorDdi;
    }

    if (!imobiliariaId) {
      errors.imobiliariaId = "Selecione a imobiliaria";
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0 || !onConfirm) return;

    await onConfirm({
      nome: nomeFinal,
      celular: celularSanitizado,
      idImobiliaria: imobiliariaId,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Cadastrar novo gerente
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Nome do gerente"
            fullWidth
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            error={Boolean(validationErrors.nome)}
            helperText={validationErrors.nome}
            disabled={saving}
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="DDI"
              value={celularDdi}
              onChange={(e) => setCelularDdi(sanitizeDdiInput(e.target.value))}
              error={Boolean(validationErrors.celularDdi)}
              helperText={validationErrors.celularDdi}
              disabled={saving}
              inputProps={{ inputMode: "numeric", maxLength: 4 }}
              sx={{ maxWidth: 140 }}
            />
            <TextField
              label="Celular"
              fullWidth
              value={celularNumero}
              onChange={(e) =>
                setCelularNumero(
                  sanitizePhoneInput(e.target.value, celularDdiDigits)
                )
              }
              error={Boolean(validationErrors.celularNumero)}
              helperText={validationErrors.celularNumero}
              disabled={saving}
              inputProps={{ inputMode: "numeric", maxLength: 13 }}
            />
          </Stack>

          <TextField
            select
            label="Imobiliaria"
            fullWidth
            value={imobiliariaId}
            onChange={(e) => setImobiliariaId(e.target.value)}
            error={Boolean(validationErrors.imobiliariaId)}
            helperText={validationErrors.imobiliariaId}
            disabled={saving || loadingImobiliarias}
          >
            <MenuItem value="">Selecione</MenuItem>
            {imobiliariasOrdenadas.map((imobiliaria, index) => (
              <MenuItem key={`${imobiliaria.id ?? "imobiliaria"}-${index}`} value={imobiliaria.id}>
                {imobiliaria.nome || "-"}
              </MenuItem>
            ))}
          </TextField>

          {errorMessage && (
            <Alert severity="error" variant="outlined">
              {errorMessage}
            </Alert>
          )}

          {success && (
            <Alert severity="success" variant="outlined">
              Gerente cadastrado com sucesso.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {saving && <CircularProgress size={20} thickness={5} />}
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={saving || success || !canSubmit}
            >
              Cadastrar
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ModalCadastroGerente;
